import {
  CreditCard,
  Fingerprint,
  HelpCircle,
  KeyRound,
  QrCode,
  Radio,
  ScanFace,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import type {
  AccessResult,
  BillingPeriod,
  DeviceStatus,
  DoorStatus,
  EventSource,
  ImportRowAction,
  InvoiceStatus,
  PersonStatus,
  ProvisioningState,
  SyncState,
  SyncSummary,
  TenantExportStatus,
  WebhookDeliveryStatus,
  WebhookEndpointStatus,
} from "@/shared/api/types";
import { accessReasonLabel } from "./access-reasons";
import { formatRelative } from "./dates";

/** State language (design-system §6): words + shape + colour, never colour alone. */
export type StateTone = "success" | "danger" | "warning" | "neutral" | "accent" | "info";

export interface StatePresentation {
  tone: StateTone;
  shape: "filled" | "hollow" | "alert";
  label: string;
}

export function deviceStatusPresentation(
  status: DeviceStatus,
  lastSeenAt: string | null,
  now: Date = new Date(),
): StatePresentation {
  switch (status) {
    case "online":
      return { tone: "success", shape: "filled", label: "Online" };
    case "offline":
      return {
        tone: "neutral",
        shape: "hollow",
        label: lastSeenAt ? `Offline · ${formatRelative(lastSeenAt, now)}` : "Offline",
      };
    case "auth_failed":
      return { tone: "danger", shape: "alert", label: "Needs attention" };
    case "pending":
      return { tone: "warning", shape: "filled", label: "Waiting for device" };
    case "disabled":
      return { tone: "neutral", shape: "hollow", label: "Disabled" };
  }
}

/**
 * Zero-touch provisioning is a visible job (rules/01 §4): pending → running → done | failed, always
 * in words — plus the fifth state, which is the honest answer when zero-touch did not happen at all.
 *
 * **"Set up on the terminal"** is deliberately neither green nor red. Red was what the desk unit
 * showed on 2026-09-17 for hours after a human had typed its push settings in by hand and events were
 * arriving: a red badge on a working terminal teaches an operator to ignore badges, which is worse
 * than no badge. Green is what the same terminal then showed once our own push credentials were
 * cleared, because the step that could not run stopped counting as a failure — and green would launder
 * a terminal with no push auth and a configuration outside our control into looking normal.
 *
 * `info` says "there is something here to know" without either claim. The words are the point: they
 * name what actually happened, they match the "Set it up on the terminal" instructions on the card
 * (which is how the operator repeats the trick on the next terminal), and in six months they tell
 * whoever debugs this box that a human, not us, owns its settings.
 */
export function provisioningPresentation(state: ProvisioningState): StatePresentation {
  switch (state) {
    case "pending":
      return { tone: "neutral", shape: "hollow", label: "Not provisioned yet" };
    case "in_progress":
      return { tone: "warning", shape: "filled", label: "Provisioning…" };
    case "provisioned":
      return { tone: "success", shape: "filled", label: "Provisioned" };
    case "configured_by_hand":
      return { tone: "info", shape: "filled", label: "Set up on the terminal" };
    case "failed":
      return { tone: "danger", shape: "alert", label: "Provisioning failed" };
  }
}

/** A closed door is the normal, secure state; open is the one to notice; unknown until the device reports. */
export function doorStatusPresentation(status: DoorStatus): StatePresentation {
  switch (status) {
    case "open":
      return { tone: "warning", shape: "filled", label: "Open" };
    case "close":
      return { tone: "success", shape: "filled", label: "Closed" };
    case "unknown":
      return { tone: "neutral", shape: "hollow", label: "Unknown" };
  }
}

/** Person × device state machine (M3 §1), always in words: the terminal's refusal rides on the row, not the chip. */
export function syncStatePresentation(state: SyncState): StatePresentation {
  switch (state) {
    case "pending":
      return { tone: "warning", shape: "filled", label: "Pending" };
    case "syncing":
      return { tone: "warning", shape: "filled", label: "Syncing…" };
    case "synced":
      return { tone: "success", shape: "filled", label: "Synced" };
    case "failed":
      return { tone: "danger", shape: "alert", label: "Failed" };
    case "deleting":
      return { tone: "warning", shape: "filled", label: "Removing…" };
    case "deleted":
      return { tone: "neutral", shape: "hollow", label: "Removed" };
  }
}

/** Rollup for the people table (design-system §6 "Syncing to 2 devices" / "Failed on Gate 2", counted). */
export function syncSummaryPresentation(sync: SyncSummary): StatePresentation {
  if (sync.total === 0) return { tone: "neutral", shape: "hollow", label: "No devices" };
  if (sync.failed > 0)
    return { tone: "danger", shape: "alert", label: `Failed ${sync.failed}/${sync.total}` };
  if (sync.pending > 0)
    return { tone: "warning", shape: "filled", label: `Pending ${sync.pending}/${sync.total}` };
  return { tone: "success", shape: "filled", label: `Synced ${sync.synced}/${sync.total}` };
}

/** Organisation lifecycle (M5, `TenantStatus`): trial → active | suspended | deleting → deleted; /me carries it untyped, so an unknown value reads as itself. */
export function tenantStatusPresentation(status: string): StatePresentation {
  switch (status) {
    case "trial":
      return { tone: "info", shape: "filled", label: "Trial" };
    case "active":
      return { tone: "success", shape: "filled", label: "Active" };
    case "suspended":
      return { tone: "danger", shape: "alert", label: "Suspended" };
    case "deleting":
      return { tone: "danger", shape: "alert", label: "Scheduled for deletion" };
    case "deleted":
      return { tone: "neutral", shape: "hollow", label: "Deleted" };
    default:
      return { tone: "neutral", shape: "hollow", label: status };
  }
}

/** A data export is a visible job (rules/01 §4): queued → running → ready | failed, always in words. */
export function exportStatusPresentation(status: TenantExportStatus): StatePresentation {
  switch (status) {
    case "queued":
      return { tone: "warning", shape: "filled", label: "Queued" };
    case "running":
      return { tone: "warning", shape: "filled", label: "Running…" };
    case "done":
      return { tone: "success", shape: "filled", label: "Ready" };
    case "failed":
      return { tone: "danger", shape: "alert", label: "Failed" };
  }
}

/**
 * The organisation's own subscription state (ADR-0015 §2): no gateway vocabulary reaches the screen any
 * more, because there is no mandate behind it — `trial` is the free window, `overdue` is money due inside
 * the grace period with everything still working, and `suspended` is after it.
 */
export function subscriptionStatusPresentation(status: string): StatePresentation {
  switch (status) {
    case "trial":
      return { tone: "info", shape: "filled", label: "Trial" };
    case "active":
      return { tone: "success", shape: "filled", label: "Active" };
    case "overdue":
      return { tone: "warning", shape: "alert", label: "Payment due" };
    case "suspended":
      return { tone: "danger", shape: "alert", label: "Paused" };
    case "ended":
      return { tone: "neutral", shape: "hollow", label: "Ended" };
    default:
      return { tone: "neutral", shape: "hollow", label: status };
  }
}

/**
 * An order's state (ADR-0015 §3). `issued` is the only one that asks anything of the customer: an invoice
 * exists and is waiting to be paid. `offline_paid` is an operator recording a payment made outside the
 * gateway, and `reverted` is a mistaken order put back — both read as settled, because they are.
 */
export function orderStatusPresentation(status: string): StatePresentation {
  switch (status) {
    case "draft":
      return { tone: "neutral", shape: "hollow", label: "Draft" };
    case "issued":
      return { tone: "warning", shape: "filled", label: "Due" };
    case "paid":
      return { tone: "success", shape: "filled", label: "Paid" };
    case "offline_paid":
      return { tone: "success", shape: "filled", label: "Paid offline" };
    case "expired":
      return { tone: "neutral", shape: "hollow", label: "Expired" };
    case "cancelled":
      return { tone: "neutral", shape: "hollow", label: "Cancelled" };
    case "reverted":
      return { tone: "neutral", shape: "hollow", label: "Reverted" };
    default:
      return { tone: "neutral", shape: "hollow", label: status };
  }
}

/** What an order is for, in the customer's words rather than the ledger's. */
export function orderKindLabel(kind: string): string {
  switch (kind) {
    case "purchase":
      return "Purchase";
    case "proration":
      return "Added mid-period";
    case "renewal":
      return "Renewal";
    case "manual":
      return "Arranged with Nexborg";
    // M20 (ADR-0024): a recharge of the SMS balance — credits, not the subscription.
    case "sms_recharge":
      return "SMS credits";
    default:
      return kind;
  }
}

/** Razorpay issues the invoices and we mirror their status (ADR-0012); "issued" is money still owed. */
export function invoiceStatusPresentation(status: InvoiceStatus): StatePresentation {
  switch (status) {
    case "paid":
      return { tone: "success", shape: "filled", label: "Paid" };
    case "partially_paid":
      return { tone: "warning", shape: "filled", label: "Partly paid" };
    case "issued":
      return { tone: "warning", shape: "filled", label: "Due" };
    case "draft":
      return { tone: "neutral", shape: "hollow", label: "Draft" };
    case "cancelled":
      return { tone: "neutral", shape: "hollow", label: "Cancelled" };
    case "expired":
      return { tone: "neutral", shape: "hollow", label: "Expired" };
    case "deleted":
      return { tone: "neutral", shape: "hollow", label: "Removed" };
  }
}

/**
 * A webhook endpoint's state (ADR-0013). The breaker has two shapes and they read differently:
 * `disabledUntil` on an *active* endpoint is the 15-minute pause that heals itself, while `disabled`
 * needs a person to fix the receiver and press Resume — so the caller passes the formatted time and
 * the reason and the words carry the difference.
 */
export function webhookEndpointPresentation(
  status: WebhookEndpointStatus,
  detail: { pausedUntil?: string | null; disabledReason?: string | null } = {},
): StatePresentation {
  if (status === "disabled") {
    return {
      tone: "danger",
      shape: "alert",
      label: detail.disabledReason ? `Disabled — ${detail.disabledReason}` : "Disabled",
    };
  }
  if (status === "paused") return { tone: "warning", shape: "filled", label: "Paused" };
  if (detail.pausedUntil) {
    return { tone: "warning", shape: "filled", label: `Paused until ${detail.pausedUntil}` };
  }
  return { tone: "success", shape: "filled", label: "Active" };
}

/** One delivery attempt (ADR-0013): a failed one still has a next attempt; a dead one has run out. */
export function webhookDeliveryPresentation(status: WebhookDeliveryStatus): StatePresentation {
  switch (status) {
    case "delivered":
      return { tone: "success", shape: "filled", label: "Delivered" };
    case "pending":
      return { tone: "warning", shape: "filled", label: "Waiting" };
    case "failed":
      return { tone: "danger", shape: "alert", label: "Failed" };
    case "dead":
      return { tone: "danger", shape: "alert", label: "Gave up" };
  }
}

export function personStatusPresentation(status: PersonStatus): StatePresentation {
  return status === "active"
    ? { tone: "success", shape: "filled", label: "Active" }
    : { tone: "neutral", shape: "hollow", label: "Disabled" };
}

/** CSV import rows: what the dry run would do, or what the commit did. */
export function importActionPresentation(action: ImportRowAction, committed: boolean): StatePresentation {
  switch (action) {
    case "create":
      return { tone: "success", shape: "filled", label: committed ? "Created" : "Create" };
    case "update":
      return { tone: "info", shape: "filled", label: committed ? "Updated" : "Update" };
    case "skip":
      return { tone: "neutral", shape: "hollow", label: committed ? "Skipped" : "Skip" };
    case "error":
      return { tone: "danger", shape: "alert", label: "Error" };
  }
}

export function accessResultLabel(result: AccessResult, errorCode: number | null): string {
  if (result === "granted") return "Granted";
  const reason = accessReasonLabel(errorCode);
  return reason ? `Denied · ${reason}` : "Denied";
}

/**
 * On-screen words and an icon per method name. `tests/guards/methods.test.ts` fails when a name in
 * `UNLOCK_METHODS` has no entry here — the "Other" fallback below is for a method a NEWER firmware
 * reports that we have never seen, not for one the server already knows how to name.
 */
export const METHOD_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  face: { label: "Face", icon: ScanFace },
  card: { label: "Card", icon: CreditCard },
  password: { label: "Password", icon: KeyRound },
  /** M16 (ADR-0021): the terminal only reads the code; the server decides and opens the door. */
  qr: { label: "QR code", icon: QrCode },
  "card+password": { label: "Card + password", icon: CreditCard },
  "password+card": { label: "Password + card", icon: KeyRound },
  remote: { label: "Remote unlock", icon: Radio },
  fingerprint: { label: "Fingerprint", icon: Fingerprint },
  "userid+password": { label: "User ID + password", icon: UserRound },
  "face+password": { label: "Face + password", icon: ScanFace },
  "card+face": { label: "Card + face", icon: ScanFace },
  "face/password": { label: "Face or password", icon: ScanFace },
  "card/face": { label: "Card or face", icon: CreditCard },
  /** M24 (ADR-0026): Face Kiosk receipts — 200 verified match, 201 manual, 202 approved override. */
  kiosk_face: { label: "Face Kiosk", icon: ScanFace },
  kiosk_manual: { label: "Face Kiosk (manual)", icon: ScanFace },
  kiosk_face_approved: { label: "Face Kiosk (approved)", icon: ScanFace },
  kiosk_phone_trusted: { label: "Face Kiosk (phone-only check)", icon: ScanFace },
};

/** The API already translates protocol codes to names; this turns names into on-screen words and an icon. */
export function methodPresentation(methodName: string): { label: string; icon: LucideIcon } {
  return (
    METHOD_LABELS[methodName] ?? { label: methodName === "unknown" ? "Unknown" : "Other", icon: HelpCircle }
  );
}

/** Where a row came from: the live upload, or the job that re-read the terminal after a disconnect (M4 §2). */
export function eventSourceLabel(source: EventSource): string {
  if (source === "face_kiosk") return "Scanned on a Face Kiosk phone";
  return source === "backfill" ? "Recovered after a disconnect" : "Received live from the terminal";
}

export function directionLabel(direction: string | null): string | null {
  if (!direction) return null;
  const d = direction.toLowerCase();
  if (d === "entry" || d === "in") return "Entry";
  if (d === "exit" || d === "out") return "Exit";
  return direction;
}

/** "Monthly" / "Yearly" — the billing period as a heading or a chip. */
export function periodNoun(period: BillingPeriod): string {
  return period === "yearly" ? "Yearly" : "Monthly";
}

/** "monthly" / "yearly" — the same word mid-sentence, where a capital would read as a heading. */
export function periodAdverb(period: BillingPeriod): string {
  return period === "yearly" ? "yearly" : "monthly";
}

/** "a month" / "a year" — what one period's total is a total OF. */
export function periodSpan(period: BillingPeriod): string {
  return period === "yearly" ? "a year" : "a month";
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Whole days from now until `at`, never negative; 0 means "today or already past". */
export function daysUntil(at: string, now: Date = new Date()): number {
  const ms = Date.parse(at) - now.getTime();
  return Number.isNaN(ms) ? 0 : Math.max(0, Math.ceil(ms / 86_400_000));
}

/** "today" / "in 1 day" / "in 6 days" — a countdown to a deadline the customer must act before. */
export function daysUntilLabel(at: string, now: Date = new Date()): string {
  const days = daysUntil(at, now);
  return days === 0 ? "today" : `in ${pluralize(days, "day")}`;
}
